import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  DEFAULT_AVATAR,
  parseAvatarForUser,
  parseProfileShowcase,
  type AvatarConfig,
  type ProfileShowcaseSlot,
} from '@shared/index'

export const USERNAME_MAX_LEN = 16
export const DEFAULT_USERNAME = 'Player'

/**
 * Trim, collapse whitespace, cap length, and fall back to Player.
 */
export function normalizeUsername(raw: unknown): string {
  if (typeof raw !== 'string') return DEFAULT_USERNAME
  const text = raw.replace(/\s+/g, ' ').trim().slice(0, USERNAME_MAX_LEN)
  return text.length > 0 ? text : DEFAULT_USERNAME
}

export const usePlayerStore = defineStore(
  'player',
  () => {
    const username = ref(DEFAULT_USERNAME)
    const avatar = ref<AvatarConfig>({ ...DEFAULT_AVATAR })
    const showcase = ref<ProfileShowcaseSlot[]>([])

    /**
     * Stores a display name used in chat and online duels.
     */
    function setUsername(raw: string): void {
      username.value = normalizeUsername(raw)
    }

    /**
     * Stores a legal portrait and frame. The crx image stays only on that name.
     */
    function setAvatar(raw: unknown): void {
      avatar.value = parseAvatarForUser(raw, username.value)
    }

    function setShowcase(raw: unknown): void {
      showcase.value = parseProfileShowcase(raw)
    }

    function reset(): void {
      username.value = DEFAULT_USERNAME
      avatar.value = { ...DEFAULT_AVATAR }
      showcase.value = []
    }

    return {
      username,
      avatar,
      showcase,
      setUsername,
      setAvatar,
      setShowcase,
      reset,
    }
  },
)
